/**
 * Single source of truth for activity icons + colors (0017).
 *
 * Each activity category maps to a Lucide icon component + a default color
 * (0008 §8.4 palette). Map markers and the web UI both resolve through here so
 * they stay consistent. Resolve a type_id to its category's visual with
 * `buildVisualForType(activityTypes, activityCategories)`.
 */
import {
  Plane,
  BedDouble,
  UtensilsCrossed,
  Camera,
  FerrisWheel,
  Martini,
  Landmark,
  Trees,
  Ticket,
  Trophy,
  ShoppingBag,
  ClipboardList,
  MapPin,
} from 'lucide-react';

// Category name → { Icon (Lucide component), color }. Keys must match the
// taxonomy seed category_name values exactly.
export const CATEGORY_VISUALS = {
  'Travel': { Icon: Plane, color: '#2563eb' },
  'Lodging': { Icon: BedDouble, color: '#0d9488' },
  'Food & Drink': { Icon: UtensilsCrossed, color: '#ea580c' },
  'Places to Explore': { Icon: Camera, color: '#c49b63' },
  'Attractions': { Icon: FerrisWheel, color: '#db2777' },
  'Entertainment & Nightlife': { Icon: Martini, color: '#7c3aed' },
  'Culture & Museums': { Icon: Landmark, color: '#6366f1' },
  'Nature & Outdoors': { Icon: Trees, color: '#16a34a' },
  'Activities & Experiences': { Icon: Ticket, color: '#0891b2' },
  'Sports': { Icon: Trophy, color: '#dc2626' },
  'Shopping': { Icon: ShoppingBag, color: '#e11d48' },
  'Logistics & Admin': { Icon: ClipboardList, color: '#64748b' },
};

// Fallback when a category is unknown/missing (also used for plain destination pins).
export const DEFAULT_VISUAL = { Icon: MapPin, color: '#c49b63' };

export function visualForCategoryName(name) {
  return CATEGORY_VISUALS[name] || DEFAULT_VISUAL;
}

/**
 * Returns (typeId) => { Icon, color, categoryName } resolving a type_id to its
 * category's visual via the loaded taxonomy.
 */
export function buildVisualForType(activityTypes, activityCategories) {
  const typeToCat = new Map(
    (Array.isArray(activityTypes) ? activityTypes : []).map((t) => [String(t.type_id), String(t.category_id)])
  );
  const catToName = new Map(
    (Array.isArray(activityCategories) ? activityCategories : []).map((c) => [
      String(c.category_id),
      String(c.category_name || ''),
    ])
  );
  return (typeId) => {
    const categoryName = catToName.get(typeToCat.get(String(typeId)) || '') || '';
    return { ...visualForCategoryName(categoryName), categoryName };
  };
}
